import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, Loader2, Pill, Repeat } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import type { PatientTreatment, PatientTreatmentStatus } from '../../types/careMessaging';
import { careThreadKey } from './DtpMessages';
import { useTrackWhen } from '../../components/analytics/useTrackWhen';

/**
 * My treatments: what the patient is on, how to take it, and a way to ask the
 * clinic for a refill. A refill request is a message in the care thread, so
 * the answer arrives where every other answer does.
 */

export const treatmentsKey = (physicianId?: string, patientId?: string) => ['patient-treatments', physicianId, patientId] as const;

type Tone = 'progress' | 'done' | 'problem' | 'neutral';
const STATUS: Record<PatientTreatmentStatus, { label: string; tone: Tone }> = {
  preparing: { label: 'Being prepared', tone: 'progress' },
  ready: { label: 'Ready at pharmacy', tone: 'progress' },
  shipped: { label: 'On its way', tone: 'progress' },
  delivered: { label: 'Delivered', tone: 'done' },
  filled: { label: 'Filled', tone: 'done' },
  on_hold: { label: 'On hold', tone: 'problem' },
  membership: { label: 'In your membership', tone: 'neutral' },
};
const PILL: Record<Tone, string> = {
  neutral: 'bg-white/80 text-[var(--portal-muted)] ring-[var(--portal-line)]',
  progress: 'bg-[var(--portal-tint-1)] text-[var(--portal-ink)] ring-transparent',
  done: 'bg-[#dcf2e3] text-[#14532d] ring-transparent',
  problem: 'bg-[#fde4dc] text-[#8c2a14] ring-transparent',
};

export function DtpTreatments() {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const treatments = useQuery<PatientTreatment[]>({
    queryKey: treatmentsKey(physicianId, patientId),
    retry: false,
    queryFn: async () => {
      const res = await dtpApi.getTreatments();
      if (!res.success || !res.data) throw new Error('Treatments unavailable');
      return res.data;
    },
  });
  useTrackWhen(treatments.isSuccess, 'treatments_viewed', { count: treatments.data?.length ?? 0 });

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="portal-display text-[36px] font-bold leading-none sm:text-[44px]">My treatments</h1>
      <p className="mt-3 text-[17px] text-[var(--portal-muted)]">How to take what your clinic prescribed, and refills when you need them.</p>

      {treatments.isPending ? (
        <div role="status" aria-label="Loading treatments" className="mt-8 space-y-4">
          {[0, 1].map(i => <div key={i} className="portal-card h-44 motion-safe:animate-pulse" />)}
        </div>
      ) : treatments.isError ? (
        <div role="alert" className="mt-8 rounded-2xl bg-[#fde4dc] p-4 text-sm text-[#8c2a14]">
          We couldn’t load your treatments. <button className="min-h-[44px] font-semibold underline" onClick={() => treatments.refetch()}>Try again</button>
        </div>
      ) : treatments.data.length === 0 ? (
        <div className="portal-card mt-8 px-6 py-16 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[18px] bg-[var(--portal-tint-1)]"><Pill aria-hidden="true" className="h-6 w-6" /></span>
          <p className="portal-display text-xl font-semibold">No treatments yet</p>
          <p className="mt-2 text-sm text-[var(--portal-muted)]">Once your clinic’s pharmacy fills a prescription for you, it shows up here with its directions.</p>
        </div>
      ) : (
        <ul className="mt-8 space-y-4">
          {treatments.data.map((t: PatientTreatment) => <li key={t.key}><TreatmentCard treatment={t} /></li>)}
        </ul>
      )}
    </div>
  );
}

/** Treatment keys can hold a drug name; ids must not hold spaces. */
const domId = (key: string) => key.replace(/[^A-Za-z0-9_-]/g, '-');

function shortDate(iso?: string) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

export function TreatmentCard({ treatment }: { treatment: PatientTreatment }) {
  const { physicianId } = useDtpParams();
  const status = STATUS[treatment.status] ?? { label: treatment.status, tone: 'neutral' as Tone };
  const headingId = `treatment-${domId(treatment.key)}`;
  return (
    <article aria-labelledby={headingId} className="portal-card p-6 sm:p-7">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--portal-tint-2)]"><Pill className="h-5 w-5" /></span>
          <h2 id={headingId} className="portal-display min-w-0 text-xl font-bold">{treatment.name}</h2>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${PILL[status.tone]}`}>{status.label}</span>
      </header>

      {treatment.directions && (
        <div className="mt-5 rounded-2xl bg-[rgba(36,26,21,0.04)] px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--portal-muted)]">Directions</p>
          <p className="mt-1 text-[15px]">{treatment.directions}</p>
        </div>
      )}

      {treatment.source === 'prescription' && (
        <dl className="mt-5 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <div><dt className="text-[var(--portal-muted)]" title="How many refills your prescriber wrote on this prescription. Your clinic can tell you how many are still available.">Refills prescribed</dt><dd className="mt-0.5 font-semibold tabular-nums">{treatment.refillsRemaining ?? '—'}</dd></div>
          <div><dt className="text-[var(--portal-muted)]">Last filled</dt><dd className="mt-0.5 font-semibold">{shortDate(treatment.lastFilledAt)}</dd></div>
          {treatment.orderNumber && <div><dt className="text-[var(--portal-muted)]">Order</dt><dd className="mt-0.5 font-semibold tabular-nums">#{treatment.orderNumber}</dd></div>}
        </dl>
      )}

      <footer className="mt-5 border-t border-[var(--portal-line)] pt-5">
        {treatment.viaMembership ? (
          <p className="flex flex-wrap items-center gap-2 text-sm text-[var(--portal-muted)]">
            <Repeat aria-hidden="true" className="h-4 w-4" />Ships with your membership each cycle, so there’s nothing to request.
            <Link to={dtpPath(physicianId, '/membership')} className="font-semibold text-[var(--portal-ink)] underline">Manage membership</Link>
          </p>
        ) : (
          <RefillAction treatment={treatment} />
        )}
      </footer>
    </article>
  );
}

function RefillAction({ treatment }: { treatment: PatientTreatment }) {
  const { physicianId } = useDtpParams();
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const messagesPath = dtpPath(physicianId, '/messages');

  if (sent || treatment.openRefillRequest) {
    const explanation = sent
      ? 'Your care team will reply in Messages.'
      : `You asked on ${shortDate(treatment.openRefillRequest?.requestedAt)}. You can ask again once your clinic replies.`;
    return (
      <div role={sent ? 'status' : undefined} className="flex flex-col items-start gap-2">
        <button type="button" disabled aria-describedby={`refill-state-${domId(treatment.key)}`}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-[rgba(36,26,21,0.06)] px-4 text-sm font-semibold text-[var(--portal-muted)]">
          <Check aria-hidden="true" className="h-4 w-4" />Refill requested
        </button>
        <p id={`refill-state-${domId(treatment.key)}`} className="text-sm text-[var(--portal-muted)]">
          {explanation}{' '}
          <Link to={messagesPath} className="inline-flex items-center gap-1 font-semibold text-[var(--portal-ink)] underline">View in Messages<ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5" /></Link>
        </p>
      </div>
    );
  }

  const noRefillsLeft = treatment.refillsRemaining === 0;
  if (impersonated) {
    return <p className="text-sm text-[var(--portal-muted)]">Only the patient can request a refill.</p>;
  }

  async function submit() {
    setBusy(true); setError('');
    try {
      const res = await dtpApi.requestRefill(treatment.key, note.trim() || undefined);
      if (!res.success) throw new Error(res.error);
      setSent(true);
      queryClient.invalidateQueries({ queryKey: treatmentsKey(physicianId, patientId) });
      queryClient.invalidateQueries({ queryKey: careThreadKey(physicianId, patientId) });
    } catch (err: any) {
      setError(err?.response?.data?.error || 'We couldn’t send your request. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="flex flex-col items-start gap-2">
        <button type="button" onClick={() => setOpen(true)}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)]">
          <Repeat aria-hidden="true" className="h-4 w-4" />Request refill
        </button>
        {noRefillsLeft && <p className="text-sm text-[var(--portal-muted)]">This prescription was written without refills, so your clinic may need to renew it first.</p>}
      </div>
    );
  }

  const noteId = `refill-note-${domId(treatment.key)}`;
  return (
    <div className="space-y-3">
      <label htmlFor={noteId} className="block text-sm font-semibold">Anything your care team should know? <span className="font-normal text-[var(--portal-muted)]">(optional)</span></label>
      <textarea id={noteId} value={note} onChange={e => setNote(e.target.value)} maxLength={500} rows={2}
        placeholder="For example: running low, about a week left"
        className="w-full resize-none rounded-2xl border border-[var(--portal-line)] bg-white/80 px-3 py-2.5 text-[15px] focus:outline-none focus:ring-4 focus:ring-[rgba(224,86,58,0.15)]" />
      {error && <p role="alert" className="text-sm text-[#8c2a14]">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={submit} disabled={busy}
          className="inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] transition hover:opacity-90 disabled:opacity-60">
          {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}Send request
        </button>
        <button type="button" onClick={() => { setOpen(false); setError(''); }}
          className="inline-flex h-10 items-center rounded-full bg-white/80 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] hover:bg-white">Cancel</button>
      </div>
    </div>
  );
}
